import Url from 'url';

import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import { color } from 'styled-system';

import { FONT_BRIGHTNESS_MAPPING } from '@hivebrite/design-system/Theme';

import { DefaultLink } from 'hb-react/frontoffice/components/Link';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';

const Item = ({
  children,
  exact,
  external = false,
  hasSubmenu = false,
  isFocusable = true,
  isSubmenu = false,
  onClick,
  onBlur,
  onFocus,
  onKeyDown,
  onKeyPress,
  openInNewTab = false,
  rootPath,
  subItems = [],
  url,
}) => {
  const { currentUrl } = useDefaultAppContexts();
  const theme = useTheme();
  const isSamePathname = useMemo(
    () =>
      url.startsWith(currentUrl.origin) &&
      Url.parse(url).pathname === window?.location?.pathname,
    [window?.location?.pathname],
  );

  const target = external && openInNewTab ? '_blank' : undefined;
  const validExternalURL =
    external && !url.startsWith('/') && !url.startsWith(currentUrl.origin);
  const useDefaultLink = validExternalURL || isSamePathname || target;
  const isActive = () =>
    window?.location?.pathname === url ||
    (subItems.length &&
      subItems.find((subItem) => subItem.url === window?.location?.pathname));

  return useDefaultLink ? (
    <DefaultLinkWrapper
      aria-current={isSamePathname ? 'page' : null}
      aria-haspopup={hasSubmenu}
      data-open-type={openInNewTab ? 'new_tab' : undefined}
      data-item-type={isSubmenu ? 'sub-menu-item' : 'menu-item'}
      data-testid="link"
      href={url}
      isSubmenu={isSubmenu}
      onClick={openInNewTab ? undefined : onClick}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onKeyPress={onKeyPress}
      role="menuitem"
      tabIndex={isFocusable && !isSubmenu ? 0 : -1}
      target={target}
    >
      {children}
    </DefaultLinkWrapper>
  ) : (
    <NavLinkWrapper
      aria-haspopup={hasSubmenu}
      data-item-type={isSubmenu ? 'sub-menu-item' : 'menu-item'}
      data-testid="nav-link"
      end={exact || url === rootPath}
      style={
        isActive()
          ? {
              color: `${theme.colors.link}`,
              textDecoration: 'underline',
            }
          : undefined
      }
      isSubmenu={isSubmenu}
      onClick={onClick}
      onBlur={onBlur}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onKeyPress={onKeyPress}
      role="menuitem"
      tabIndex={isFocusable && !isSubmenu ? 0 : -1}
      target={target}
      to={url.replace(currentUrl.origin, '')}
    >
      {children}
    </NavLinkWrapper>
  );
};

Item.propTypes = {
  exact: PropTypes.bool,
  external: PropTypes.bool,
  hasSubmenu: PropTypes.bool,
  isFocusable: PropTypes.bool,
  isSubmenu: PropTypes.bool,
  onClick: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyPress: PropTypes.func,
  openInNewTab: PropTypes.bool,
  rootPath: PropTypes.string,
  subItems: PropTypes.array,
  url: PropTypes.string.isRequired,
};

export default Item;

const DefaultLinkWrapper = styled(DefaultLink)
  .withConfig({
    shouldForwardProp: (prop) => !['isSubmenu'].includes(prop),
  })
  .attrs(({ theme, isSubmenu }) => ({
    color: isSubmenu
      ? theme.colors.black
      : FONT_BRIGHTNESS_MAPPING.asphalt[theme.themeVariables.fontBrightness],
  }))`
  ${color}

  ${({ isSubmenu, theme }) => css`
    display: inline-block;
    font-size: ${theme.fontSizes[2]};
    font-weight: 600;
    margin-right: ${isSubmenu ? 0 : '24px'};
    padding: ${isSubmenu ? '8px 12px' : '8px 0'};

    &:hover {
      color: ${(props) =>
        isSubmenu
          ? props.theme.colors.white
          : props.theme.colors.link} !important;
      text-decoration: underline;
    }
  `}
`;

const NavLinkWrapper = styled(NavLink)
  .withConfig({
    shouldForwardProp: (prop) => !['isSubmenu'].includes(prop),
  })
  .attrs(({ theme, isSubmenu }) => ({
    color: isSubmenu
      ? theme.colors.black
      : FONT_BRIGHTNESS_MAPPING.asphalt[theme.themeVariables.fontBrightness],
  }))`
  ${color}

  ${({ isSubmenu, theme }) => css`
    display: inline-block;
    font-size: ${theme.fontSizes[2]};
    font-weight: 600;
    margin-right: ${isSubmenu ? 0 : '24px'};
    padding: ${isSubmenu ? '8px 12px' : '8px 0'};

    &:hover {
      color: ${(props) =>
        isSubmenu
          ? props.theme.colors.white
          : props.theme.colors.link} !important;
      text-decoration: underline;
    }
  `}
`;
